/**
 * Glaslatten: geprijsd per meter, maar besteld per BOS.
 *
 * Timmers levert glaslatten alleen in bossen van een vast aantal lengtes, en hoevéél lengtes dat
 * zijn hangt af van het type — de letter(+cijfer) waarmee de artikelnaam begint (A1, C2, N3, O11).
 * Hoe lang die lengtes zijn wisselt per levering (2,15 tot 5,5 m), dus de exacte meters in een bos
 * weten we pas nadat we Timmers gebeld hebben. Daarom: prijs per meter tonen, eerlijk zeggen dat
 * er per bos geleverd wordt, en de regel als offerte de deur uit doen.
 *
 * Bos-aantallen zoals opgegeven door Timmers (28-07-2026):
 *   A t/m F        → 20 lengtes
 *   N 0 t/m 5      → 10 lengtes
 *   N 8 t/m 12     →  5 lengtes
 *   O 1 t/m 6      → 20 lengtes
 *   O 10 t/m 13    → 10 lengtes
 *   V              → 10 lengtes
 *   SV             → uitlopend; aantal op aanvraag
 */

/**
 * Subgroepen (Timmers) met houten latten die per bos gaan. De type-letter volgt de subgroep:
 * A t/m F zijn "Glaslat", de O-reeks "Opdeklat", de N-reeks "Neuslat" en de V-reeks
 * "Ventilatielat". Aluminium beglazingsprofielen en de Venti-stone-artikelen vallen erbuiten:
 * die worden per stuk verkocht, niet per meter uit een bos.
 */
export const GLASLAT_SUBGROEPEN: readonly string[] = ["Glaslat", "Opdeklat", "Neuslat", "Ventilatielat"];
/** @deprecated Gebruik GLASLAT_SUBGROEPEN — losse subgroep bleef hier voor bestaande imports. */
export const GLASLAT_SUBGROEP = "Glaslat";
/** Bandbreedte van een handelslengte als de omschrijving niets zegt (mm). */
export const GLASLAT_LENGTE_MIN_MM = 2150;
export const GLASLAT_LENGTE_MAX_MM = 5500;

export interface GlaslatBos {
  /** Aantal lengtes in één bos; null = niet af te leiden uit de naam. */
  perBos: number | null;
  /** Uitlopend type: het aantal per bos vragen we per order aan Timmers. */
  uitlopend: boolean;
}

/** Is dit een houten lat die per bos besteld wordt? */
export function isGlaslat(art: { subgroep?: string }): boolean {
  return !!art.subgroep && GLASLAT_SUBGROEPEN.includes(art.subgroep);
}

/**
 * Hoeveel lengtes zitten er in een bos van dit artikel? Leest het type uit het begin van de naam.
 * Onbekend type → null; dan noemen we het aantal niet in plaats van er een te verzinnen.
 */
export function glaslatBos(name: string | undefined): GlaslatBos {
  const m = (name ?? "").trim().match(/^([A-Za-z]{1,2})\s*(\d{1,2})?\b/);
  if (!m) return { perBos: null, uitlopend: false };
  const letters = m[1]!.toUpperCase();
  const nr = m[2] != null ? Number(m[2]) : null;
  if (letters === "SV") return { perBos: null, uitlopend: true };
  if (letters === "V") return { perBos: 10, uitlopend: false };
  if (letters === "N") {
    if (nr != null && nr >= 0 && nr <= 5) return { perBos: 10, uitlopend: false };
    if (nr != null && nr >= 8 && nr <= 12) return { perBos: 5, uitlopend: false };
    return { perBos: null, uitlopend: false };
  }
  if (letters === "O") {
    if (nr != null && nr >= 1 && nr <= 6) return { perBos: 20, uitlopend: false };
    if (nr != null && nr >= 10 && nr <= 13) return { perBos: 10, uitlopend: false };
    return { perBos: null, uitlopend: false };
  }
  // A t/m F, maar alleen als het écht één letter is: "DL51" is een ander type dan "D3".
  if (letters.length === 1 && letters >= "A" && letters <= "F") return { perBos: 20, uitlopend: false };
  return { perBos: null, uitlopend: false };
}

export interface GlaslatLengtes {
  minMm: number;
  maxMm: number;
  /** Staat het in de productomschrijving, of is het de algemene bandbreedte? */
  uitOmschrijving: boolean;
}

/**
 * Lengtebereik van dit type. Bij een deel van de artikelen staat het in de omschrijving
 * ("leverbaar in lengtes van 2450 tot 5200 mm"); anders de algemene bandbreedte.
 */
export function glaslatLengtes(omschrijving: string | undefined): GlaslatLengtes {
  const kaal = (omschrijving ?? "").replace(/<[^>]+>/g, " ");
  const m = kaal.match(/lengtes?\s+van\s+(\d{3,4})\s*(?:mm)?\s*(?:tot|t\/m|-|–)\s*(\d{3,4})\s*mm/i);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (a > 0 && b >= a) return { minMm: a, maxMm: b, uitOmschrijving: true };
  }
  return { minMm: GLASLAT_LENGTE_MIN_MM, maxMm: GLASLAT_LENGTE_MAX_MM, uitOmschrijving: false };
}

export interface GlaslatBosBerekening {
  /** Aantal bossen dat je nodig hebt om zeker genoeg meters te hebben. */
  bossen: number;
  /** Wat je minimaal en maximaal aan meters in huis krijgt met dat aantal bossen. */
  minMeters: number;
  maxMeters: number;
}

/**
 * Hoeveel bossen heb je nodig voor `meters`? Gerekend met de KÓRTSTE lengte: komt er een bos met
 * korte latten, dan heeft de klant nog steeds genoeg. Vallen ze langer uit, dan houdt hij over —
 * dat vertellen we er eerlijk bij in plaats van het risico andersom te leggen.
 */
export function glaslatBossenVoorMeters(meters: number, perBos: number, lengtes: GlaslatLengtes): GlaslatBosBerekening | null {
  if (!(meters > 0) || !(perBos > 0) || !(lengtes.minMm > 0)) return null;
  const perBosMinM = (perBos * lengtes.minMm) / 1000;
  const perBosMaxM = (perBos * lengtes.maxMm) / 1000;
  const bossen = Math.max(1, Math.ceil(meters / perBosMinM));
  return {
    bossen,
    minMeters: Math.round(bossen * perBosMinM * 10) / 10,
    maxMeters: Math.round(bossen * perBosMaxM * 10) / 10,
  };
}
